import { AgentRouting, VoiceAiProvider } from '@pypestream/api-services/urql';
import {
  Pype,
  PypeBeta,
  PypeConfig,
  PypeStyle,
} from '../xstate/prostudio.api-service.types';
import { decodeFromBase64 } from './encode-base64';

export interface Channel {
  id: string;
  type?: 'widget' | 'twilio_voice';
  parent: string;
  env: 'live' | 'sandbox';
  streamId: string;
  pypeId: string;
  logo: string;
  agentRouting: PypeConfig['agent_routing'];
  name: string;
  config?: PypeConfig['config'];
  botRetry?: PypeConfig['bot_retry'];
  style?: PypeStyle;
  beta?: PypeBeta;
  lastEdited?: string;
  customPypeCss?: string;
  twilioVoiceConfig?: {
    account_sid: string;
    auth_token: string;
    handoff_url: string;
    init_call_webhook_url: string;
    language: string;
    phone_number: string;
    speech_model: string;
    transcription_provider: string;
    tts_provider: string;
    voice: string;
    welcome_greeting: string;
    elevenlabs_audio_model: string;
    elevenlabs_voice_id: string;
    elevenlabs_similarity: number;
    elevenlabs_speed: number;
    elevenlabs_stability: number;
    elevenlabs_voice_metadata_b64?: string;
    elevenlabs_voice_metadata?: VoiceAiProvider;
  };
}

export const getChannelsFromPypes = (pypes: Pype[]): Channel[] => {
  return (
    pypes?.flatMap(
      (pype) =>
        pype.streams?.flatMap((stream): Channel[] => {
          if (stream.twilio_voice_enabled) {
            return [
              {
                id: stream.id || '',
                type: 'twilio_voice' as const,
                streamId: stream.id || '',
                parent: pype.name || '',
                env: pype.env,
                pypeId: pype.id || '',
                logo: pype.logo || '',
                agentRouting: pype.agent_routing as AgentRouting,
                name: stream.name || '',
                twilioVoiceConfig: (() => {
                  const config =
                    stream.twilio_voice_config as Channel['twilioVoiceConfig'];
                  if (
                    config?.elevenlabs_voice_metadata_b64 &&
                    config?.elevenlabs_voice_metadata_b64 !== 'null'
                  ) {
                    try {
                      const decodedMetadata = JSON.parse(
                        decodeFromBase64(config.elevenlabs_voice_metadata_b64)
                      );
                      return {
                        ...config,
                        elevenlabs_voice_metadata: decodedMetadata,
                      };
                    } catch (error) {
                      console.error(
                        'Failed to decode elevenlabs voice metadata:',
                        error
                      );
                      return config;
                    }
                  }
                  return config;
                })(),
                lastEdited: '',
              },
            ];
          }
          return (
            stream.widgets?.map((widget) => ({
              type: 'widget',
              parent: pype.name || '',
              env: pype.env,
              streamId: stream.id || '',
              pypeId: pype.id || '',
              logo: pype.logo || '',
              agentRouting: pype.agent_routing as AgentRouting,
              config:
                pype.config?.integration_headers?.account_id &&
                pype.config?.integration_headers?.inbox_id
                  ? {
                      ...pype.config,
                      integration_headers: pype.config.integration_headers,
                    }
                  : {
                      integration_headers: {
                        account_id: '',
                        inbox_id: '',
                      },
                    },
              botRetry: pype.bot_retry,

              id: widget.id || '',
              name: widget.widget_name || '',
              lastEdited:
                new Date(widget.last_updated || '').toISOString() || '',
              style: widget.style ? JSON.parse(widget.style) : undefined,
              beta: widget.beta ? JSON.parse(widget.beta) : undefined,
              customPypeCss: widget.custom_pype_css || '',
            })) || []
          );
        }) || []
    ) || []
  );
};
